// Date-based status badges ("Previews", "Opening Day!", "Closing Soon", ...)
// shared by the dashboard deal rows and the All Shows list.

export type ShowStatusBadge =
  | 'preview_opening_day'
  | 'in_previews'
  | 'opening_day'
  | 'recently_opened'
  | 'closing_day'
  | 'final_week'
  | 'closing_soon'
  | null;

// A show counts as newly opened for this many days after its opening date,
// mirroring the 14-day "Closing Soon" window at the other end of the run.
const RECENTLY_OPENED_DAYS = 14;

// Any object carrying the three run dates as YYYY-MM-DD strings
export interface ShowRunDates {
  preview_date: string | null;
  opening_date: string | null;
  close_date: string | null;
}

const midnight = (isoDate: string): Date => {
  const d = new Date(isoDate + 'T12:00:00');
  d.setHours(0, 0, 0, 0);
  return d;
};

const today = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Which badge (if any) applies to a show on a given date.
 *
 * `checkDate` is midnight-normalized — on the dashboard it's the date of the
 * showtime the deal is for, elsewhere it defaults to today.
 */
export const getStatusBadgeForDates = (
  dates: ShowRunDates | undefined | null,
  checkDate?: Date
): ShowStatusBadge => {
  if (!dates) return null;

  const check = checkDate ?? today();
  const sameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

  if (dates.preview_date) {
    if (sameDay(check, midnight(dates.preview_date))) return 'preview_opening_day';
  }

  if (dates.opening_date) {
    const openingDate = midnight(dates.opening_date);
    if (sameDay(check, openingDate)) return 'opening_day';
    if (check < openingDate) {
      // In previews window: after preview_date (if known) and before opening_date
      if (dates.preview_date) {
        if (check > midnight(dates.preview_date)) return 'in_previews';
      } else {
        return 'in_previews';
      }
    }
  } else if (dates.preview_date) {
    // Has preview but no opening date yet — in previews if on or after preview_date
    if (check >= midnight(dates.preview_date)) return 'in_previews';
  }

  if (dates.close_date) {
    const closeDate = midnight(dates.close_date);
    if (sameDay(check, closeDate)) return 'closing_day';
    const daysUntilClose = Math.ceil((closeDate.getTime() - check.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilClose >= 0 && daysUntilClose <= 7) return 'final_week';
    if (daysUntilClose >= 0 && daysUntilClose <= 14) return 'closing_soon';
  }

  // Checked last so that an imminent close still wins: on a short limited run a
  // show can be both newly opened and about to close, and closing is the more
  // useful thing to surface.
  if (dates.opening_date) {
    const daysSinceOpening = Math.round(
      (check.getTime() - midnight(dates.opening_date).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceOpening > 0 && daysSinceOpening <= RECENTLY_OPENED_DAYS) return 'recently_opened';
  }

  return null;
};

/** Same lookup keyed by show name against the /shows/status-flags map. */
export const getShowStatusBadge = (
  showName: string,
  flags: Record<string, ShowRunDates> | undefined,
  dealDate?: Date
): ShowStatusBadge => (flags ? getStatusBadgeForDates(flags[showName], dealDate) : null);
